import { Button, Highlight, Stack, Tag } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { QueueItemState, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { copyInputToClipboard } from '@mews-commander/utils';
import { ModalSidePanel } from '../../../../components';
import { useQueueItemsDetailsData } from '../../queries/useQueueItemsDetailsData';
import { QueueItemQueryParams } from '../../types';
import { getInfoBanner, stateTranslations, tagVariants } from './utils';
import { EnterpriseInfo } from './EnterpriseInfo';

interface QueueItemDetailPanelProps {
    id: string;
    state: QueueItemState;
    onClose: () => void;
    opened: boolean;
    setQueueItemParams: (nextState: QueueItemQueryParams) => void;
}

export const QueueItemDetailPanel = ({ onClose, opened, id, state, setQueueItemParams }: QueueItemDetailPanelProps) => {
    const { data: queueItemData, isFetching } = useQueueItemsDetailsData(id);
    const invalidateQueueItemsData = useInvalidateRepositoryQuery();

    const handleOnClose = () => {
        if (queueItemData && queueItemData.state !== state) {
            setQueueItemParams({ id, state: queueItemData.state });
            void invalidateQueueItemsData('enterpriseBuilder', 'getQueueItemsOverview');
        }

        onClose();
    };

    const handleCopy = () => {
        copyInputToClipboard(JSON.stringify(queueItemData?.outputData, null, 2));
    };

    return (
        <ModalSidePanel
            title={
                queueItemData && (
                    <>
                        {queueItemData.enterpriseName}
                        <Tag outline variant={tagVariants[queueItemData.state]}>
                            {lt(stateTranslations[queueItemData.state])}
                        </Tag>
                    </>
                )
            }
            content={
                queueItemData?.inputData && (
                    <>
                        <Stack vertical spacing="300">
                            {getInfoBanner(queueItemData.state, queueItemData.inputData.data.discriminator)}
                            <Highlight size="medium">{lt('DetailPlural')}</Highlight>
                            <EnterpriseInfo
                                jobId={queueItemData.id}
                                queueItemDetail={queueItemData}
                                selectedEntities={queueItemData?.inputData.data.value.entitiesSelected}
                            />
                        </Stack>
                    </>
                )
            }
            actions={
                queueItemData?.state === QueueItemState.Success && (
                    <Stack alignment="end">
                        <Button size="medium" type="submit" onClick={handleCopy} variant="primary">
                            {lt('Copy')}
                        </Button>
                    </Stack>
                )
            }
            onClose={handleOnClose}
            opened={opened}
            headerActions={null}
            width="medium"
            loading={isFetching}
        />
    );
};
